import React, { useEffect, useMemo, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { ChannelPageHero, ChannelEmpty } from '@/pages/WatchPage';
import { useGlobalAudio } from '@/components/GlobalAudioPlayer';
import { Play, Pause, Mic2, ListMusic, Shuffle } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * PodcastsPage — Phase 17 mirror of MusicPage.
 * Every episode is a first-class card with Play Now. Play All enters Playlist
 * Mode. Shuffle All randomizes the queue on entry. All controls are shared
 * with the global audio player, so behavior matches Music exactly.
 */
export default function PodcastsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    current, isPlaying, togglePlay, playTrack, playAll,
    shuffle, setShuffle, playlistMode,
  } = useGlobalAudio();

  useEffect(() => {
    api.get('/audio').then(({ data }) => {
      const eps = (data || []).filter((a) => a.kind === 'podcast');
      setItems(eps);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const playable = useMemo(
    () => items.filter((it) => it.file_url || (it.source_type === 'embed' && it.embed_url)),
    [items]
  );

  const handlePlay = (track) => {
    if (current?.id === track.id) { togglePlay(); return; }
    playTrack(track);
  };
  const handlePlayAll = () => {
    if (playable.length === 0) return;
    playAll(playable);
  };
  const handleShuffleAll = () => {
    if (playable.length === 0) return;
    if (!shuffle) setShuffle(true);
    playAll(playable);
  };

  return (
    <div data-testid="podcasts-page">
      <ChannelPageHero
        kicker="CHANNEL — PODCASTS"
        title="PODCASTS"
        blurb="Episodes, conversations, long-form thinking. Press play — G is talking."
        accent="gold"
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-32">
        {loading ? (
          <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Fetching episodes…</div>
        ) : items.length === 0 ? (
          <ChannelEmpty title="ON DECK" copy="Episodes will land here. Subscribe to be first in the room." />
        ) : (
          <>
            <PodcastsHeader
              count={playable.length}
              onPlayAll={handlePlayAll}
              onShuffleAll={handleShuffleAll}
              playlistMode={playlistMode}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="podcasts-grid">
              {items.map((it) => {
                const active = current?.id === it.id;
                const showPlay = !active || !isPlaying;
                const disabled = !(it.file_url || (it.source_type === 'embed' && it.embed_url));
                return (
                  <PodcastCard
                    key={it.id}
                    episode={it}
                    active={active}
                    showPlay={showPlay}
                    disabled={disabled}
                    onPlay={() => !disabled && handlePlay(it)}
                    isPlaying={isPlaying && active}
                  />
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function PodcastsHeader({ count, onPlayAll, onShuffleAll, playlistMode }) {
  return (
    <div className="pg-card p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="podcasts-header">
      <div className="min-w-0">
        <p className="pg-label text-[10px]" style={{ color: 'var(--pg-gold-2)' }}>PODCAST FEED</p>
        <p className="text-[11px] font-mono tracking-widest text-[var(--pg-ink-faint)] mt-1">
          {count} EPISODE{count === 1 ? '' : 'S'}{playlistMode ? ' · PLAYLIST MODE' : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onShuffleAll}
          data-testid="podcasts-shuffle-all"
          className="pg-btn-ghost rounded-[var(--pg-radius-md)] px-3 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"
        >
          <Shuffle size={13} /> SHUFFLE ALL
        </button>
        <button
          onClick={onPlayAll}
          data-testid="podcasts-play-all"
          className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"
        >
          <ListMusic size={13} /> PLAY ALL
        </button>
      </div>
    </div>
  );
}

function PodcastCard({ episode, active, showPlay, disabled, onPlay, isPlaying }) {
  return (
    <motion.div
      initial={{ opacity: 1, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={`group pg-card overflow-hidden transition-colors ${active ? 'border-[var(--pg-gold-2)]' : 'hover:border-[rgba(226,199,122,0.4)]'}`}
      style={{ boxShadow: active ? '0 12px 40px rgba(0,0,0,0.55), 0 0 24px rgba(226,199,122,0.20)' : undefined }}
      data-testid="podcast-card"
      data-active={active ? '1' : '0'}
    >
      <div className="flex items-start gap-4 p-4">
        <div className="relative h-24 w-24 sm:h-28 sm:w-28 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]">
          {episode.cover_url ? (
            <img src={mediaUrl(episode.cover_url)} alt={episode.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[var(--pg-gold-2)]"><Mic2 size={28} /></div>
          )}
          <button
            onClick={onPlay}
            disabled={disabled}
            data-testid="podcast-card-play"
            aria-label={showPlay ? `Play ${episode.title}` : `Pause ${episode.title}`}
            className={`absolute inset-0 flex items-center justify-center transition-opacity ${
              disabled ? 'opacity-40 cursor-not-allowed'
              : active ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100'
            }`}
            style={{ background: 'rgba(4,6,12,0.4)' }}
          >
            <span className={`h-11 w-11 rounded-full flex items-center justify-center ${disabled ? 'bg-[var(--pg-navy-800)] text-[var(--pg-ink-faint)]' : 'bg-[var(--pg-gold-2)] text-[var(--pg-navy-900)]'}`}>
              {showPlay ? <Play size={16} className="ml-0.5" /> : <Pause size={16} />}
            </span>
          </button>
          {active && isPlaying && (
            <span className="absolute top-2 left-2 inline-flex items-end gap-[2px] h-4 rounded-full bg-[rgba(4,6,12,0.55)] px-1.5 py-1" aria-hidden>
              <span className="pg-bar" />
              <span className="pg-bar" style={{ animationDelay: '0.2s' }} />
              <span className="pg-bar" style={{ animationDelay: '0.4s' }} />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-mono tracking-widest text-[var(--pg-gold-2)] uppercase">Episode</p>
          <p className={`font-serif text-base sm:text-lg leading-snug mt-1 line-clamp-2 ${active ? 'pg-text-glow-gold' : 'text-[var(--pg-ink)]'}`}>
            {episode.title}
          </p>
          {episode.description && <p className="text-xs text-[var(--pg-ink-muted)] mt-2 line-clamp-3">{episode.description}</p>}
          {episode.duration && <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase mt-2">{episode.duration}</p>}
        </div>
      </div>
    </motion.div>
  );
}
